import { useTranslation } from "react-i18next";

export default function HeroSection() {
  const { t, i18n } = useTranslation();
  const isJa = i18n.language === "ja";

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative w-full h-screen min-h-[700px] flex overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/6f4c822fc37adee3ce52c9757882221d.jpg"
          alt="New Jersey Skyline"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient: strong left for text readability, fades out on right */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/35" />
      </div>

      {/* Two-column layout */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-14 lg:px-20 grid grid-cols-12 items-center">

        {/* Left: text block — 7 of 12 cols */}
        <div className="col-span-12 lg:col-span-7 mt-16">
          {/* Gold accent line */}
          <div className="w-8 h-px bg-[#BF9B5E] mb-5" />

          {/* Label */}
          <p className="text-[#BF9B5E] text-[11px] font-semibold tracking-[0.3em] uppercase mb-5">
            {t("hero_label")}
          </p>

          {/* Main heading */}
          <h1 className="text-white mb-6" style={{ lineHeight: 1.1 }}>
            <span
              className={`block font-extrabold tracking-tight ${
                isJa
                  ? "text-3xl md:text-4xl lg:text-[2.75rem]"
                  : "text-4xl md:text-5xl lg:text-[3.5rem]"
              }`}
            >
              {t("hero_title_line1")}
            </span>
          </h1>

          {/* Divider */}
          <div className="w-12 h-px bg-white/30 mb-6" />

          {/* Subtitle */}
          <p className="text-white/70 text-sm md:text-base leading-relaxed mb-10 max-w-sm lg:max-w-md">
            {t("hero_subtitle")}
          </p>

          {/* CTAs */}
          <div className="flex flex-row items-center gap-4 flex-wrap">
            <button
              onClick={() => scrollTo("cta")}
              className="px-7 py-3.5 bg-[#BF9B5E] text-white text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-[#a8854a] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("hero_cta")}
            </button>
            <button
              onClick={() => scrollTo("background")}
              className="px-7 py-3.5 border border-white/50 text-white text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("hero_scroll")}
            </button>
          </div>
        </div>

        {/* Right: intentionally empty — shows the city skyline */}
        <div className="col-span-5 hidden lg:block" />
      </div>

      {/* Scroll indicator — bottom center */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-40">
        <span className="text-white text-[9px] tracking-[0.25em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-white animate-pulse" />
      </div>
    </section>
  );
}
